import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Menu from 'components/global/Menu';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useUpdateEffect from 'hooks/useUpdateEffect';

import axios from 'services/axios';

import AppLogger from 'utils/AppLogger';

import { faArrowLeftRotate, faChevronDown, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';

import { FilterType, convertFilterValueToDTO } from './DynamicListFilterUtils';
import * as S from './DynamicListTable.styled';

const DynamicListColumnSummary = ({ footerGroups, fetchUrl, filters, loading }) =>
	footerGroups.map((footerGroups, i) => (
		<S.TableRow key={`footer_row_${i}`} {...footerGroups.getFooterGroupProps()} className="column-calculation-row">
			{footerGroups.headers.map((column, j) => (
				<React.Fragment key={`footer_row_${i}_column${j}`}>
					<div className="th" {...column.getFooterProps()}>
						{j === 0 &&
						column.filterType !== FilterType.number &&
						column.filterType !== FilterType.float ? (
							<Typography fontWeight="500">total</Typography>
						) : !loading &&
						  (column.filterType === FilterType.number || column.filterType === FilterType.float) ? (
							<SummaryCell column={column} fetchUrl={fetchUrl} filters={filters} />
						) : undefined}
					</div>
					<S.ResizerSpace />
				</React.Fragment>
			))}
		</S.TableRow>
	));

const SummaryCell = ({ column, fetchUrl, filters }) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [value, setValue] = useState(null);
	const { showError } = useAlert();
	const [menuRef, setMenuRef] = useState(null);
	const [selectedOption, setSelectedOption] = useState(null);

	const handleFetchSummary = useCallback((type) => {
		setLoading(true);

		axios
			.post(fetchUrl, {
				filter:
					filters?.length > 0 ? filters.map((filter) => convertFilterValueToDTO(filter, false)) : undefined,
				columnSummary: [
					{
						key: column.id,
						type
					}
				]
			})
			.then((response) => {
				if (response?.status === 200) {
					setValue(response?.data?.data?.[0]?.[column.id] ?? null);
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setLoading(false);
			});
	}, []);

	useUpdateEffect(() => {
		setValue(null);
	}, [filters]);

	return (
		<S.ColumnSummaryContainer>
			{loading ? (
				<S.ColumnSummaryBox>
					<Icon icon={faSpinnerThird} spin color="var(--black)" size="large" />
				</S.ColumnSummaryBox>
			) : value !== null ? (
				<S.ColumnSummaryBox ref={setMenuRef}>
					<Typography fontWeight="500">{value}</Typography>
					<Typography color="var(--content-3)">({t(`columnSummary.${selectedOption}`)})</Typography>
					<S.ColumnSummaryBoxIcon icon={faArrowLeftRotate} />
				</S.ColumnSummaryBox>
			) : (
				<Button
					ref={setMenuRef}
					icon={faChevronDown}
					iconPos="right"
					variant="transparent"
					label="calculate"
					className="column-summary-calculate-button"
				/>
			)}
			{menuRef && (
				<Menu
					placement="bottom-start"
					buttonRef={menuRef}
					menuItems={[
						{
							label: 'columnSummary.sum',
							action: () => {
								handleFetchSummary('sum');
								setSelectedOption('sum');
							}
						},
						{
							label: 'columnSummary.avg',
							action: () => {
								handleFetchSummary('avg');
								setSelectedOption('avg');
							}
						},
						{
							label: 'columnSummary.min',
							action: () => {
								handleFetchSummary('min');
								setSelectedOption('min');
							}
						},
						{
							label: 'columnSummary.max',
							action: () => {
								handleFetchSummary('max');
								setSelectedOption('max');
							}
						}
					]}
				/>
			)}
		</S.ColumnSummaryContainer>
	);
};

export default DynamicListColumnSummary;
